import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCookieConsent } from '../contexts/CookieConsentContext'
import { XMarkIcon, Cog6ToothIcon } from '@heroicons/react/24/outline'

const CookieBanner = () => {
  const {
    consent,
    showBanner,
    showSettings,
    acceptAll,
    acceptNecessary,
    updateConsent,
    saveSettings,
    openSettings,
    closeSettings
  } = useCookieConsent()

  if (!showBanner && !showSettings) return null

  return (
    <>
      {/* Main Banner */}
      {showBanner && !showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-slate-200 shadow-lg lg:pl-72">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <p className="text-sm text-slate-700">
                  Мы используем cookie для работы сайта, аналитики и персонализации.
                  Подробнее в{' '}
                  <Link to="/cookies" className="text-brand-lime-700 hover:underline font-medium">
                    Политике cookie
                  </Link>.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 bg-brand-lime text-brand-black text-sm font-semibold rounded-lg hover:bg-brand-lime-600 transition-colors"
                >
                  Принять все
                </button>
                <button
                  onClick={acceptNecessary}
                  className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Только необходимые
                </button>
                <button
                  onClick={openSettings}
                  className="px-4 py-2 bg-white text-slate-600 text-sm font-semibold rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors flex items-center gap-1"
                >
                  <Cog6ToothIcon className="w-4 h-4" />
                  Настроить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Настройки cookie</h2>
              <button
                onClick={closeSettings}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              <p className="text-sm text-slate-600">
                Выберите, какие категории cookie вы разрешаете использовать.
                Необходимые cookie нельзя отключить — они требуются для работы сайта.
              </p>

              {/* Necessary Cookies */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">Необходимые</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Авторизация, сессия, настройки конфиденциальности
                    </p>
                  </div>
                  <div className="text-sm text-slate-400 font-medium">
                    Всегда активны
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">Аналитические</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Яндекс.Метрика — понимание использования сайта
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => updateConsent('analytics', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-brand-lime rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-lime"></div>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">Маркетинговые</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Отслеживание рекламных кампаний, реферальные ссылки
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => updateConsent('marketing', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-brand-lime rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-slate-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-lime"></div>
                  </label>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Подробнее о каждой категории cookie в нашей{' '}
                <Link to="/cookies" className="text-brand-lime-700 hover:underline">
                  Политике cookie
                </Link>.
              </p>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <button
                onClick={acceptNecessary}
                className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-200 transition-colors"
              >
                Отклонить опциональные
              </button>
              <button
                onClick={saveSettings}
                className="px-4 py-2 bg-brand-lime text-brand-black text-sm font-semibold rounded-lg hover:bg-brand-lime-600 transition-colors"
              >
                Сохранить настройки
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CookieBanner
